import { Dropdown } from '@honeyscience/honey-ui-toolkit';
import React, {
  useEffect, ReactElement, Fragment, useState, useMemo,
} from 'react';
import { createUseStyles } from 'react-jss';
import { useLocation, useHistory } from 'react-router-dom';
import Store from 'mockStores/components/stores/Store';
import { MockStoresWindow } from 'mockStores/types';
import { isPaypalEnvSandbox, isPaypalEnvStaging, PaypalEnvs } from 'types';

declare let window: MockStoresWindow;

const availableEnvironments = [PaypalEnvs.Sandbox, PaypalEnvs.Staging].map(env => ({
  key: env,
  label: env,
}));

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function getClientId(selectedEnv: number) {
  if (isPaypalEnvSandbox(availableEnvironments[selectedEnv].key)) {
    return process.env.PAYPAL_CLIENT_IDS[PaypalEnvs.Sandbox];
  }
  return process.env.PAYPAL_CLIENT_IDS[PaypalEnvs.Staging];
}

function appendScript(src, onload, selectedEnv) {
  const script = document.createElement('script');
  script.defer = true;
  script.src = src;

  if (isPaypalEnvSandbox(availableEnvironments[selectedEnv].key)) {
    script.setAttribute('data-env', 'sandbox');
  }

  if (isPaypalEnvStaging(availableEnvironments[selectedEnv].key)) {
    script.setAttribute('data-env', 'stage');
    script.setAttribute('data-stage', 'te-honey-paypal');
  }

  if (onload) {
    script.onload = onload;
  }
  document.head.appendChild(script);
}

function getEnv(selectedEnv: number) {
  if (isPaypalEnvSandbox(availableEnvironments[selectedEnv].key)) {
    return 'sandbox';
  }
  if (isPaypalEnvStaging(availableEnvironments[selectedEnv].key)) {
    return 'stage';
  }
  return 'production';
}

const useStyles = createUseStyles({
  paypalButton: {
    paddingTop: '8px',
  },
});

function SPBv4(): ReactElement {
  const classes = useStyles();
  const query = useQuery();
  const env = query.get('env');
  const history = useHistory();

  let querriedEnv = availableEnvironments.findIndex(e => e.key === env);
  if (querriedEnv < 0) {
    querriedEnv = 0;
  }

  const [selectedEnv, setSelectedEnv] = useState(querriedEnv);

  const handleInputOnChangeEnvironment = (options): void => {
    const params = new URLSearchParams({ env: options.key });
    setSelectedEnv(availableEnvironments.findIndex(e => e.key === options.key));
    history.replace({ search: params.toString() });
    window.location.reload();
  };

  function renderPayPalButton() {
    const buttonConfig = {
      client: {
        [getEnv(selectedEnv)]: getClientId(selectedEnv),
      },
      env: getEnv(selectedEnv),
      fundingSource: window.paypal.FUNDING.PAYPAL,
      onApprove(data, actions) {
        console.log('MERCHANT APPROVE ORDER', data, actions); // eslint-disable-line no-console
        history.push('/continue');
      },
      onCancel() {
        console.log('MERCHANT CANCEL ORDER'); // eslint-disable-line no-console
      },
      onError(err) {
        console.log('MERCHANT ERROR ORDER', err); // eslint-disable-line no-console
      },
      payment(data, actions) {
        console.log('MERCHANT CREATE ORDER', data, actions); // eslint-disable-line no-console
        return actions.payment.create({
          payment: {
            transactions: [{
              amount: {
                currency: 'USD',
                total: '0.01',
              },
            }],
          },
        });
      },
      style: {
        height: 40,
        shape: 'rect',
      },
    };
    window.paypal.Button.render(buttonConfig, '#paypal-button-container');
  }

  useEffect(() => {
    /* v4 merchant SDK loads from different URL than the Honey modal which uses v5: */
    appendScript('https://www.paypalobjects.com/api/checkout.js', renderPayPalButton, selectedEnv);
  }, []);

  const renderButton = () => (
    <div id="paypal-button-container" className={ classes.paypalButton } />
  );

  return (
    <Fragment>
      <Store
        button={ renderButton() }
        title="Honey Checkout Mock Store (SPBv4)"
      />
      <p> PayPal Environment: </p>
      <Dropdown
        id="environment-dropdown"
        label="Select environment"
        onChange={ handleInputOnChangeEnvironment }
        options={ availableEnvironments }
        selectedOption={ selectedEnv }
        size={ 32 }
        showLabel
      />
    </Fragment>
  );
}

export default SPBv4;
